<template>
    <div id="financeView">
        <div class="container-fluid">
            <div class="row">
                <div class="col-12">

                    <!-- Header Meta !-->
                    <table-meta-element
                        :hide-nav="true"
                        :hide-nav-on-no-pages="false"
                        title="Finance"
                        item-type="finance deals"
                        :show-loading="true"
                    >
                        <div slot="title" class="dropdown" style="display: inline-block;">
                            <title-view-selector
                                :title="title"
                                :options="viewOptions"
                                @select="viewSelected"
                            ></title-view-selector>
                        </div>

                        <div slot="pills">
                            <div style="display: flex; justify-content: end; align-items: center;">
                                <a
                                    v-if="selectedRecords.length > 0"
                                    href="#"
                                    class="pill pill-sm pill-dark"
                                    title="Update Payments"
                                    style="margin-right: 1rem; white-space: nowrap;"
                                    @click.prevent="openBulkUpdate"
                                >
                                    <i class="material-icons">payments</i>
                                    <span class="pill-label no-left-padding">
                                        Update {{ selectedRecords.length }}
                                        {{ selectedRecords.length === 1 ? 'payment' : 'payments' }}
                                    </span>
                                </a>

                                <ui-textbox
                                    icon="search"
                                    type="search"
                                    placeholder="Reg, Customer, Fin. Co"
                                    v-model="searchTerms"
                                    @input="doSearch"
                                    style="width: 220px; max-width: 300px; margin-bottom: 0;"
                                ></ui-textbox>
                            </div>
                        </div>
                    </table-meta-element>
                </div>
            </div>
        </div>

        <!-- Instance Grid !-->
<!--        <div class="row">-->
<!--            <div class="col-12" style="padding: 0;">-->
                <grid
                    :config="gridConfig"
                    :data="gridRows"
                    style="width: 100%;"
                ></grid>
<!--            </div>-->
<!--        </div>-->

        <!-- Contra Modal !-->
        <finance-agreement-contra-modal ref="contraModal" @saved="get"></finance-agreement-contra-modal>

        <!-- Bulk Payment Update Modal !-->
        <ui-modal
            ref="bulkUpdateModal"
            size="small"
            title="Bulk Update"
            :remove-close-button="!bulk.isRunning"
            @hide="resetBulkUpdate"
            :class="[bulk.isRunning ? 'finance-update-running' : '']"
        >
            <div v-if="!bulk.isRunning && bulk.errors.length === 0">
                <!-- Balance !-->
                <ui-checkbox
                    label="Balance Status"
                    v-model="bulk.updateBalance"
                ></ui-checkbox>

                <div v-if="bulk.updateBalance" class="finance-update-section">
                    <ui-select
                        label="Payment Status"
                        placeholder="Select status..."
                        :options="paymentStatusOptions"
                        v-model="bulk.status"
                    ></ui-select>

                    <ui-datepicker
                        v-if="isBankedStatus"
                        label="Payment Date"
                        :start-of-week="1"
                        :custom-formatter="dateFormatter"
                        append-dropdown-to-body
                        v-model="bulk.date"
                    ></ui-datepicker>
                </div>

                <!-- Commission !-->
                <ui-checkbox
                    label="Commission Status"
                    v-model="bulk.updateCommission"
                ></ui-checkbox>

                <div v-if="bulk.updateCommission" class="finance-update-section">
                    <ui-select
                        label="Commission Payment Method"
                        placeholder="Select method..."
                        :options="commissionMethodOptions"
                        v-model="bulk.commissionMethod"
                    ></ui-select>

                    <ui-datepicker
                        label="Commission Paid Date"
                        :start-of-week="1"
                        :custom-formatter="dateFormatter"
                        append-dropdown-to-body
                        v-model="bulk.commissionDate"
                    ></ui-datepicker>

                    <p v-if="commissionBlockedByStatus" class="finance-update-note">
                        A payment has to be banked before its commission can be recorded, so
                        the payment status has to be set to Banked to update both together.
                    </p>

                    <p v-else-if="commissionSkipCount > 0" class="finance-update-note">
                        {{ commissionSkipCount }} of the selected
                        {{ commissionSkipCount === 1 ? 'payment has' : 'payments have' }} not
                        been banked yet, so
                        {{ commissionSkipCount === 1 ? 'its commission' : 'their commissions' }}
                        will be skipped.
                    </p>
                </div>
            </div>

            <!-- Progress !-->
            <div v-if="bulk.isRunning" style="text-align: center; padding: 1rem 0;">
                <ui-progress-circular
                    v-if="bulkProgress > 0"
                    type="determinate"
                    :progress="bulkProgress"
                    :size="80"
                    :stroke="3"
                    style="margin: auto;"
                ></ui-progress-circular>
                <ui-progress-circular
                    v-else
                    type="indeterminate"
                    :size="80"
                    :stroke="2"
                    style="margin: auto;"
                ></ui-progress-circular>

                <p style="margin-top: 1rem; margin-bottom: 0;">
                    Updating {{ bulk.completed }} of {{ bulk.total }}&hellip;
                </p>
            </div>

            <!-- Errors !-->
            <div v-if="bulk.errors.length > 0 && !bulk.isRunning">
                <p>
                    {{ bulk.errors.length }} of {{ bulk.total }}
                    {{ bulk.errors.length === 1 ? 'payment' : 'payments' }} could not be updated.
                    The rest were saved.
                </p>

                <ul class="finance-bulk-errors">
                    <li v-for="error in bulk.errors" :key="error.label">
                        <strong>{{ error.label }}</strong> &mdash; {{ error.message }}
                    </li>
                </ul>
            </div>

            <div slot="footer">
                <ui-button
                    v-if="!bulk.isRunning && bulk.errors.length === 0"
                    color="primary"
                    :disabled="!canApplyBulkUpdate"
                    @click="applyBulkUpdate"
                >
                    Update
                </ui-button>

                <ui-button
                    v-if="bulk.errors.length > 0 && !bulk.isRunning"
                    @click="$refs.bulkUpdateModal.close()"
                >
                    Close
                </ui-button>
            </div>
        </ui-modal>
    </div>
</template>

<style lang="scss">
    .finance-agreements {
        .content { margin-left: -15px; margin-right: -15px; footer { display: none; } }
        .secondary-tab-nav { margin-left: 15px; margin-right: 15px; }
        .meta-container > .row { margin-left: 0; margin-right: 0; }
    }

    #financeView {
        .dk-bryntum-grid {
            font-size: 14px;

            & > div { height: calc(100vh - 210px - 52px) !important; }

            .b-grid-footer-summary { text-align: left; }

            #b-numberfield-1-input { padding: 0;}
            label[for="b-numberfield-1-input"] { margin-bottom: 0;}

            #b-pagingtoolbar-1 { border: 1px solid #d8d9da; }
        }

        .finance-grid-save {
            color: #ccc;

            &.is-dirty { color: #12c457; cursor: pointer; }
        }

        .finance-grid-saving {
            color: #999;
            animation: finance-grid-spin 1s linear infinite;
        }
    }

    .finance-update-running {
        .ui-modal__footer { display: none; }
    }

    .finance-update-section {
        margin: .5rem 0 1.25rem;
        padding-left: 1.75rem;
    }

    .finance-update-note {
        margin-bottom: 0;
        font-size: .85rem;
        color: #de6a6a;
    }

    .finance-bulk-errors {
        margin: 0;
        padding-left: 1.25rem;
        max-height: 260px;
        overflow-y: auto;

        li { margin-bottom: .25rem; }
    }

    @keyframes finance-grid-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
</style>

<script>
    import DataView from '../../components/mixins/DataView';
    import { DealResource, DealLifeCycleStatuses } from '../../classes/Api/Deal';
    import { PaymentResource } from '../../classes/Api/Payment';
    import { mapState, mapActions } from 'vuex'
    import FinanceAgreementContraModal from "./FinanceAgreementContraModal.vue";
    import Grid from "../elements/grid/Grid";
    import { cloneDeep, debounce } from "lodash";
    import Moment from "moment";
    import DealerKit from "../../api";
    import {
        applyBulkCommission,
        applyBulkStatus,
        commissionPaymentMethods,
        financeStatuses,
        isBanked,
        relationsToLoad,
        rowLabel,
        rowProcessor,
        useGridConfig,
        usePagingBridge,
    } from "./FinanceGridConfig";

    export default {
        components: {FinanceAgreementContraModal, Grid},
        mixins: [DataView],
        data() {
            return {
                searchTerms: '',
                gridRows: [],
                selectedRecords: [],
                bulk: {
                    // Either half of the update can be run on its own, or both together.
                    updateBalance: true,
                    // ui-select's value prop is required and rejects null.
                    status: '',
                    date: null,
                    updateCommission: false,
                    commissionMethod: '',
                    commissionDate: null,
                    isRunning: false,
                    completed: 0,
                    total: 0,
                    errors: [],
                },
            }
        },
        watch: {
            /**
             * The grid edits its rows in place. Those rows are detached copies of the
             * result set so that editing never mutates Vuex state, which would otherwise
             * invalidate this list and force the store to reload mid-edit.
             */
            results: {
                immediate: true,
                handler() {
                    if (!this.hasResults) {
                        this.gridRows = []

                        return
                    }

                    const seen = new Set()

                    // The grid keys its rows by deal id, so a page that carries the same
                    // deal twice would leave it holding two rows under one id, which its
                    // own bookkeeping cannot untangle, besides counting the deal twice in
                    // the column totals.
                    this.gridRows = this.results.results.reduce((rows, deal) => {
                        if (!seen.has(deal.id)) {
                            seen.add(deal.id)
                            rows.push(rowProcessor(cloneDeep(deal)))
                        }

                        return rows
                    }, [])
                }
            }
        },
        created() {
            this.setResourceRelations(relationsToLoad);

            this.setFilter({
                key: 'deal.finance_status',
                value: 'unbanked'
            })

            this.setFilter({
                key: 'deal.life_cycle_status',
                value: [
                    DealLifeCycleStatuses.DRAFT,
                    DealLifeCycleStatuses.IN_PROGRESS,
                    DealLifeCycleStatuses.COMPLETED,
                ]
            })

            this.setDefaultSortOrder('id DESC');

            // Must be set before the initial load so the first request asks for 50.
            this.setPerPage(50);

            this.setDefaultViewAndLoad('Un-Banked', DealResource);
        },
        methods: {
            ...mapActions({
                get: 'dataView/get'
            }),
            showContraForAgreement(agreement) {
                this.$refs.contraModal.open(agreement)
            },
            /**
             * Persists a single row. The record's data object is the same one the grid
             * edits in place, so the flags/dates written by the editors are already on it.
             */
            persistRecord(record) {
                const payment = record.data.finance_payment
                const agreement = payment.finance_agreement

                const payload = {
                    ...payment,
                    paid_at: this.toApiDate(payment.paid_at),
                    commission_amount: agreement.commission_amount,
                    finance_agreement: {
                        ...agreement,
                        commission_paid_at: this.toApiDate(agreement.commission_paid_at),
                    },
                }

                return PaymentResource.update(payment.id, payload).then(() => {
                    return DealerKit.financeCommission.putFinanceCommission(agreement.id, {
                        amount: agreement.commission_amount,
                        paid_at: this.toApiDate(agreement.commission_paid_at),
                        payment_method: agreement.commission_payment_method,
                    })
                })
            },
            save(record) {
                record.set('is_busy', true)

                this.persistRecord(record).then(() => {
                    record.set({is_busy: false, is_dirty: false})
                    this.get()
                }).catch(e => {
                    handleApiError(e);
                    record.set('is_busy', false)
                })
            },
            /**
             * Copied rather than assigned: the grid hands back its selection collection's
             * internal array, the same reference every time, so assigning it directly
             * leaves Vue with nothing to react to.
             */
            selectionChanged(selection) {
                this.selectedRecords = selection.slice()
            },
            openBulkUpdate() {
                this.resetBulkUpdate()
                this.$refs.bulkUpdateModal.open()
            },
            resetBulkUpdate() {
                this.bulk.updateBalance = true
                this.bulk.status = ''
                this.bulk.date = null
                this.bulk.updateCommission = false
                this.bulk.commissionMethod = ''
                this.bulk.commissionDate = null
                this.bulk.isRunning = false
                this.bulk.completed = 0
                this.bulk.total = 0
                this.bulk.errors = []
            },
            /**
             * Writes the chosen balance and/or commission details onto every selected row
             * and saves each one down the same path as the per row save button. Rows are
             * done a few at a time so a large selection does not sit through a hundred
             * sequential round trips.
             */
            applyBulkUpdate() {
                if (!this.canApplyBulkUpdate) {
                    return
                }

                const records = this.selectedRecords.slice()
                const status = this.bulk.updateBalance ? this.selectedStatus : null
                const date = this.isBankedStatus ? this.bulk.date : null
                const commissionMethod = this.bulk.updateCommission ? this.selectedCommissionMethod : null

                this.bulk.errors = []
                this.bulk.completed = 0
                this.bulk.total = records.length
                this.bulk.isRunning = true

                const queue = records.slice()

                const next = () => {
                    const record = queue.shift()

                    if (!record) {
                        return Promise.resolve()
                    }

                    // Checked before anything is written, so a row that cannot take the
                    // commission is left exactly as it was rather than half updated.
                    if (commissionMethod && !this.willBeBanked(record)) {
                        this.bulk.errors.push({
                            label: rowLabel(record),
                            message: 'The payment has to be banked before its commission can be recorded.',
                        })
                        this.bulk.completed++

                        return next()
                    }

                    // Wrapped so that a row which throws on the way in, rather than on the
                    // request, is reported like any other failure. Thrown from here it
                    // would reject the worker and leave the modal running for good, and
                    // its close button is hidden while it runs.
                    return Promise.resolve().then(() => {
                        if (status) {
                            applyBulkStatus(record, status, date)
                        }

                        // Last: applyBulkStatus() clears the commission fields.
                        if (commissionMethod) {
                            applyBulkCommission(record, commissionMethod, this.bulk.commissionDate)
                        }

                        return this.persistRecord(record)
                    }).then(() => {
                        record.set({is_busy: false, is_dirty: false})
                    }).catch(e => {
                        this.bulk.errors.push({
                            label: rowLabel(record),
                            message: this.errorMessage(e),
                        })
                    }).then(() => {
                        this.bulk.completed++

                        return next()
                    })
                }

                const workers = Array.from(
                    {length: Math.min(4, records.length)}, () => next()
                )

                Promise.all(workers).then(() => {
                    if (this.bulk.errors.length === 0) {
                        // Close before clearing isRunning, otherwise the controls flash
                        // back into view for a frame. State is reset by the hide handler.
                        this.$refs.bulkUpdateModal.close()
                    } else {
                        this.bulk.isRunning = false
                    }

                    this.get()
                })
            },
            /**
             * Whether the row will be banked once the update has been applied, which is
             * the point at which its commission becomes recordable.
             */
            willBeBanked(record) {
                return this.bulk.updateBalance
                    ? this.isBankedStatus
                    : isBanked(record.data)
            },
            /**
             * ui-select hands back the whole option object when the options are objects.
             */
            optionValue(selection) {
                return selection && typeof selection === 'object' ? selection.value : selection
            },
            errorMessage(e) {
                return e?.response?.data?.message
                    ?? e?.message
                    ?? 'The update failed.'
            },
            toApiDate(value) {
                return value ? Moment(value).format() : null
            },
            /**
             * The pickers default to a long form date, the grid's date columns show the
             * same dates as DD/MM/YYYY.
             */
            dateFormatter(date) {
                return Moment(date).format('DD/MM/YYYY')
            },
            doSearch: debounce(function(term) {
                if (term && term.length > 0) {
                    this.setFilter({
                        key: 'deal.search',
                        value: term
                    })
                } else {
                    this.removeFilter('deal.search')
                }

                // The result set shrinks as the terms are typed, so staying on the
                // current page would ask for a page the matches no longer reach.
                this.setPage(1)

                this.get()
            }, 500),
        },
        computed: {
            ...mapState({
                title: state => state.dataView.title,
                currentFilters: state => state.dataView.currentFilters,
            }),
            paymentStatusOptions() {
                return financeStatuses
            },
            commissionMethodOptions() {
                return commissionPaymentMethods
            },
            selectedStatus() {
                return this.optionValue(this.bulk.status)
            },
            selectedCommissionMethod() {
                return this.optionValue(this.bulk.commissionMethod)
            },
            isBankedStatus() {
                return this.selectedStatus === 'banked'
            },
            /**
             * Marking the balance as anything but banked clears the commission alongside
             * it, so the two halves would contradict each other.
             */
            commissionBlockedByStatus() {
                return this.bulk.updateCommission
                    && this.bulk.updateBalance
                    && !!this.selectedStatus
                    && !this.isBankedStatus
            },
            /**
             * Selected rows the commission update would have to skip, so the modal can say
             * so up front rather than listing them all as failures afterwards. Only counted
             * when the balance is left alone, since a balance update decides the banked
             * state for every row at once and is covered by commissionBlockedByStatus.
             */
            commissionSkipCount() {
                if (!this.bulk.updateCommission || this.bulk.updateBalance) {
                    return 0
                }

                return this.selectedRecords.filter(record => !isBanked(record.data)).length
            },
            /**
             * Either half can be run on its own, so each is only validated when it is on.
             * Every status but banked clears the payment date, so only banked needs one,
             * and the commission API rejects a method without a date or the other way round.
             */
            canApplyBulkUpdate() {
                if (this.bulk.isRunning || (!this.bulk.updateBalance && !this.bulk.updateCommission)) {
                    return false
                }

                if (this.bulk.updateBalance
                    && (!this.selectedStatus || (this.isBankedStatus && !this.bulk.date))) {
                    return false
                }

                if (this.bulk.updateCommission) {
                    if (!this.selectedCommissionMethod || !this.bulk.commissionDate) {
                        return false
                    }

                    // Nothing would be left to do once every row has been ruled out.
                    if (this.commissionBlockedByStatus
                        || this.commissionSkipCount === this.selectedRecords.length) {
                        return false
                    }
                }

                return true
            },
            bulkProgress() {
                return this.bulk.total > 0
                    ? Math.round((this.bulk.completed / this.bulk.total) * 100)
                    : 0
            },
            gridConfig() {
                return useGridConfig({
                    showContra: this.showContraForAgreement,
                    save: this.save,
                    selectionChanged: this.selectionChanged,
                    attachPaging: grid => usePagingBridge(grid, this.$store, () => this.get()),
                })
            },
            viewOptions() {
                return [
                    {
                        label:'Un-Banked',
                        filters: {
                            'deal.finance_status': 'unbanked',
                            'deal.life_cycle_status': [
                                DealLifeCycleStatuses.DRAFT,
                                DealLifeCycleStatuses.IN_PROGRESS,
                                DealLifeCycleStatuses.COMPLETED,
                            ]
                        },
                        sort: 'id DESC'
                    },
                    {
                        label:'Un-Paid Commission',
                        filters: {
                            'deal.finance_commission_payment_status': 0,
                            'deal.life_cycle_status': [
                                DealLifeCycleStatuses.COMPLETED,
                            ]
                        },
                        sort: 'handoverBooking.from DESC'
                    },
                    {
                        type: 'divider'
                    },
                    {
                        label:'Awaiting Approval',
                        filters: {
                            'deal.finance_status': 'waiting',
                            'deal.life_cycle_status': [
                                DealLifeCycleStatuses.DRAFT,
                                DealLifeCycleStatuses.IN_PROGRESS,
                                DealLifeCycleStatuses.COMPLETED,
                            ]
                        },
                        sort: 'id DESC'
                    },
                    {
                        label:'Un-Signed',
                        filters: {
                            'deal.finance_status': 'unsigned',
                            'deal.life_cycle_status': [
                                DealLifeCycleStatuses.DRAFT,
                                DealLifeCycleStatuses.IN_PROGRESS,
                                DealLifeCycleStatuses.COMPLETED,
                            ]
                        },
                        sort: 'id DESC'
                    },
                    {
                        label:'Signed',
                        filters: {
                            'deal.finance_status': 'signed',
                            'deal.life_cycle_status': [
                                DealLifeCycleStatuses.DRAFT,
                                DealLifeCycleStatuses.IN_PROGRESS,
                                DealLifeCycleStatuses.COMPLETED,
                            ]
                        },
                        sort: 'id DESC'
                    },
                    {
                        label:'Paid Out',
                        filters: {
                            'deal.finance_status': 'paid',
                            'deal.life_cycle_status': [
                                DealLifeCycleStatuses.DRAFT,
                                DealLifeCycleStatuses.IN_PROGRESS,
                                DealLifeCycleStatuses.COMPLETED,
                            ]
                        },
                        sort: 'created_at DESC'
                    },
                    {
                        label:'Banked',
                        filters: {
                            'deal.finance_status': 'banked',
                            'deal.life_cycle_status': [
                                DealLifeCycleStatuses.DRAFT,
                                DealLifeCycleStatuses.IN_PROGRESS,
                                DealLifeCycleStatuses.COMPLETED,
                            ]
                        },
                        sort: 'handoverBooking.from DESC'
                    }
                ].concat(this.savedViewMenuOptions);
            },
        }
    }
</script>
